// Audience detection — pure function over quiz variables. Used by both:
//   • netlify/lib/reportGenerator.ts (server, drives Gemini prompt tuning)
//   • src/routes/Pricing.tsx          (client, reframes Pricing copy)
// Keep the detection logic in ONE place so server-side prompt tuning and
// client-side copy always match.

export type Audience =
  | 'high-school'
  | 'college-student'
  | 'recent-grad'
  | 'returning'
  | 'professional';

export function detectAudience(variables: Record<string, string>): Audience {
  const studentStatus = variables.studentStatus ?? '';
  const employmentHistory = variables.employmentHistory ?? '';
  const employmentStatus = variables.employmentStatus ?? '';
  const purpose = variables.testTakingPurpose ?? '';
  const ageRaw = variables.currentAge ?? '';
  const age = parseInt(ageRaw, 10);

  // Explicit student declarations.
  if (studentStatus === 'middle' || studentStatus === 'high') return 'high-school';
  if (studentStatus === 'college' || studentStatus === 'graduate') return 'college-student';
  if (purpose === 'collegeMajor') return 'college-student';

  // Never been employed + young age → recent grad.
  if (employmentHistory === 'false') {
    if (Number.isFinite(age) && age <= 25) return 'recent-grad';
    return 'returning';
  }

  if (purpose === 'return') return 'returning';

  if (employmentStatus === 'fullTime' || employmentStatus === 'partTime') return 'professional';

  // Default: treat as professional (most common case for our funnel).
  return 'professional';
}

/** Coarse-grained grouping useful when "high-school" vs "college" doesn't matter. */
export function audienceGroup(audience: Audience): 'student' | 'grad' | 'returning' | 'professional' {
  if (audience === 'high-school' || audience === 'college-student') return 'student';
  if (audience === 'recent-grad') return 'grad';
  if (audience === 'returning') return 'returning';
  return 'professional';
}
